import { useQuery, UseQueryOptions } from "react-query";
import { clearAccessToken, getAccessToken } from "./api";

export enum AccessOutcome {
  HasAccessWithData = "HasAccessWithData",
  HasAccessNoData = "HasAccessNoData",
  NoAccess = "NoAccess",
}

export interface ClientAccess {
  clients: Array<string>;
  accessOutcome: AccessOutcome;
  brandDisplayName: string | null;
}

interface ClientAccessResponse {
  data?: {
    clients?: Array<string> | null;
    accessOutcome?: string | null;
    brandDisplayName?: string | null;
  };
}

const DEFAULT_CLIENT_ACCESS: ClientAccess = {
  clients: [],
  accessOutcome: AccessOutcome.NoAccess,
  brandDisplayName: null,
};

const parseAccessOutcome = (value?: string | null): AccessOutcome => {
  if (value === AccessOutcome.HasAccessWithData) {
    return AccessOutcome.HasAccessWithData;
  }

  if (value === AccessOutcome.HasAccessNoData) {
    return AccessOutcome.HasAccessNoData;
  }

  if (value === AccessOutcome.NoAccess) {
    return AccessOutcome.NoAccess;
  }

  return AccessOutcome.NoAccess;
};

export const useClientAccess = <T = ClientAccess>(options?: UseQueryOptions<ClientAccess, Error, T>) => {
  const fetchClientAccess = async (): Promise<ClientAccess> => {
    const response = await window.fetch(`${window.origin}/api/Payer/ClientAccess`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAccessToken();
        window.location.href = "/";
        throw new Error("Unauthorized");
      }

      throw new Error(`Failed to fetch client access: ${response.status}`);
    }

    const payload = (await response.json()) as ClientAccessResponse;
    const data = payload.data;

    if (!data) {
      return DEFAULT_CLIENT_ACCESS;
    }

    return {
      clients: Array.isArray(data.clients) ? data.clients : [],
      accessOutcome: parseAccessOutcome(data.accessOutcome),
      brandDisplayName: data.brandDisplayName?.trim() ? data.brandDisplayName.trim() : null,
    };
  };

  return useQuery<ClientAccess, Error, T>("client-access", fetchClientAccess, options);
};
